import {
  Center,
  HStack,
  IconButton,
  Spacer,
  Spinner,
  Stack,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { CloseIcon, SFPlusCircleIcon } from '@assets/icons';
import { ACCEPTED_DOC_FILE_FORMATS } from '@util/constants';
import { useUserStore } from '@store/app.store';
import { useMutationToast, useS3Bucket } from '@util/hooks';
import DocumentIcon from '@components/general/document-icon.component';
import S3Bucket from '@services/s3bucket.service';
import {
  useAddMeetingAttachment,
  useRemoveMeetingAttachments
} from '@services/query-services/meetings.query-service';
import DeleteConfirmationModal from '@components/general/modals/delete-confirmation-modal.component';
import DocumentUploadModal from '@components/general/modals/document-upload-modal.component';
import AddButton from '@components/general/add-button.component';

const MeetingAttachments = ({ meeting }) => {
  return (
    <Stack>
      <Text variant='content-section-header'>Attachments</Text>
      {meeting?.attachments?.map((attachment) => (
        <AttachmentCard
          key={JSON.stringify(attachment)}
          meeting={meeting}
          attachment={attachment}
        />
      ))}
      <AddAttachment meeting={meeting} />
    </Stack>
  );
};

const AttachmentCard = ({ meeting, attachment }) => {
  const queryClient = useQueryClient();
  const user = useUserStore((state) => state.user);
  const tenant = useUserStore((state) => state.tenant);
  const { onSuccess: onDeleteSuccess, onError: onDeleteError } = useMutationToast(
    'attachment',
    'delete'
  );
  const { mutateAsync: removeAttachment, isLoading: isRemovingAttachment } =
    useRemoveMeetingAttachments({
      onSuccess: () => {
        const meetingAllQueryKey = ['meetings', { id: meeting?.id }, 'all'];
        const meetingAll = queryClient.getQueryData(meetingAllQueryKey);

        const newMeetingAll = {
          ...meetingAll,
          value: {
            ...meetingAll.value,
            attachments: meetingAll.value.attachments?.filter((a) => a.key !== attachment?.key)
          }
        };
        queryClient.setQueryData(meetingAllQueryKey, newMeetingAll);
        onDeleteSuccess();
      },
      onError: onDeleteError
    });
  const [isOpening, setIsOpening] = useState(false);
  const deleteDisclosure = useDisclosure();

  const onLinkClick = async () => {
    setIsOpening(true);
    const s3 = new S3Bucket('email-parser-processing', {
      queryClient,
      user,
      tenant
    });

    const url = await s3.getFileDownloadUrl(attachment?.key);
    setIsOpening(false);
    window.open(url, '_blank');
  };

  const onAttachmentDelete = async () => {
    await removeAttachment({
      id: meeting?.id,
      params: { sync: true },
      body: [{ key: attachment?.key }]
    });
    deleteDisclosure.onClose();
  };

  return (
    <HStack
      role='group'
      layerStyle='card'
      rounded='none'
      borderRadius='lg'
      py={2}
      px={4}
    >
      <DocumentIcon extension={ACCEPTED_DOC_FILE_FORMATS[attachment?.type]?.[0]?.substring(1)} />
      <Text
        cursor='pointer'
        _hover={{ textDecor: 'underline', color: 'brandPrimary.primary' }}
        color='surface.onSurface'
        noOfLines={1}
        variant='body-600-sm'
        onClick={onLinkClick}
      >
        {attachment?.name}
      </Text>
      <Spacer />
      <Center boxSize={5}>
        {isOpening ? (
          <Spinner
            color='brandPrimary.primary'
            size='xs'
          />
        ) : (
          <IconButton
            data-testid='remove-attachment-button'
            visibility='hidden'
            _groupHover={{ visibility: 'visible' }}
            isRound
            colorScheme='surface'
            variant='reskinGhost'
            size='xs'
            icon={<CloseIcon />}
            onClick={deleteDisclosure.onOpen}
          />
        )}
      </Center>
      <DeleteConfirmationModal
        disclosure={deleteDisclosure}
        headerText='Remove Attachment?'
        onConfirmDelete={onAttachmentDelete}
        isDeleting={isRemovingAttachment}
      >
        <Text>Are you sure you want to remove this attachment?</Text>
      </DeleteConfirmationModal>
    </HStack>
  );
};

const AddAttachment = ({ meeting }) => {
  const disclosure = useDisclosure();
  const s3 = useS3Bucket('email-parser-processing');
  const user = useUserStore((s) => s.user);
  const tenant = useUserStore((s) => s.tenant);
  const { mutateAsync: addAttachment } = useAddMeetingAttachment();

  return (
    <>
      <DocumentUploadModal
        disclosure={disclosure}
        headerIcon={
          <SFPlusCircleIcon
            boxSize={7}
            p={1}
          />
        }
        headerText='Add attachment to a meeting'
        submitLabel='Upload'
        submitTestId='upload-attachment-button'
        infoToastTitle={(file) => `Attaching file ${file?.name}`}
        onSubmit={async ({ file, fileName }) => {
          const customFileKey = `${tenant.id}/${file.name}`;
          await s3.uploadFile(file, { fileName, fileKey: customFileKey });
          await addAttachment({
            id: meeting?.id,
            params: { sync: true },
            body: {
              key: customFileKey,
              name: fileName,
              type: file.type,
              creator: { id: user?.id }
            }
          });
        }}
      />

      <AddButton
        text='Add Attachment'
        onClick={disclosure.onOpen}
        testId='add-attachment-button'
      />
    </>
  );
};

export default MeetingAttachments;
